import React, { useState, useEffect } from 'react';
import { Card, Typography, Select, Space, Row, Col, Tag, Spin } from 'antd';
import { useParams, Link } from 'react-router-dom';
import { MapContainer, TileLayer } from 'react-leaflet';
import { Breadcrumb } from 'antd';
import { getPlotsApi } from '../../api/crops';
import 'leaflet/dist/leaflet.css';

const { Title, Text } = Typography;

const INDICES = [
  { value: 'NDVI', label: 'NDVI — Crop Health', description: 'Normalized Difference Vegetation Index. Higher = healthier crop.', colors: ['#d73027', '#fc8d59', '#fee08b', '#91cf60', '#1a9850'] },
  { value: 'CGVI', label: 'CGVI — Chlorophyll', description: 'Chlorophyll/early-mid growth detection.' },
  { value: 'NDRE', label: 'NDRE — Nitrogen', description: 'Nitrogen stress / mid-late growth.' },
  { value: 'NDWI', label: 'NDWI — Water', description: 'Water levels & irrigation needs.', colors: ['#8c510a', '#bf812d', '#dfc27d', '#80cdc1', '#003c30'] },
];

const LEGEND_NDVI = [
  { color: '#d73027', label: 'Very Poor' },
  { color: '#fc8d59', label: 'Poor' },
  { color: '#fee08b', label: 'Moderate' },
  { color: '#91cf60', label: 'Good' },
  { color: '#1a9850', label: 'Excellent' },
];

export default function GISViewer() {
  const { farmId, plotId } = useParams();
  const [selectedIndex, setSelectedIndex] = useState('NDVI');
  const [center] = useState([23.0225, 72.5714]);
  const [plot, setPlot] = useState(null);
  const [loadingPlot, setLoadingPlot] = useState(false);
  const currentIndex = INDICES.find((i) => i.value === selectedIndex);

  useEffect(() => {
    if (!plotId) return;
    setLoadingPlot(true);
    getPlotsApi({ id: plotId })
      .then((res) => {
        const list = res.data?.data || [];
        const found = Array.isArray(list)
          ? list.find((p) => String(p.id) === String(plotId))
          : list;
        setPlot(found || null);
      })
      .catch(() => {})
      .finally(() => setLoadingPlot(false));
  }, [plotId]);

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 16 }} items={[
        { title: <Link to="/farms">Farms</Link> },
        { title: <Link to={`/farms/${farmId}/farm`}>Crops</Link> },
        { title: 'Satellite / GIS' },
      ]} />

      {/* Plot name above the map */}
      <div style={{ marginBottom: 16 }}>
        {loadingPlot ? (
          <Spin size="small" />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Title level={4} style={{ margin: 0, color: '#1a6b2a' }}>
              📍 {plot?.name || 'Plot'}
            </Title>
            {plot?.farm_name && (
              <Tag color="green" style={{ fontSize: 13 }}>{plot.farm_name}</Tag>
            )}
            {plot?.seted_area && (
              <Text type="secondary" style={{ fontSize: 13 }}>
                {plot.seted_area} {plot.seted_area_unit}
              </Text>
            )}
            {plot?.latest_crop_name && (
              <Tag color="lime">🌱 {plot.latest_crop_name}</Tag>
            )}
          </div>
        )}
        <Title level={5} style={{ margin: '4px 0 0', color: '#595959', fontWeight: 400 }}>
          🛰️ Satellite / GIS Data
        </Title>
      </div>

      <Row gutter={16}>
        <Col xs={24} lg={18}>
          <Card style={{ borderRadius: 10, padding: 0, overflow: 'hidden' }}>
            <div style={{ height: 500 }}>
              <MapContainer center={center} zoom={14} style={{ height: '100%', width: '100%' }}>
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  attribution="Esri"
                />
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" opacity={0.3} />
              </MapContainer>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={6}>
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <Card title="Index" style={{ borderRadius: 10 }}>
              <Select value={selectedIndex} onChange={setSelectedIndex} style={{ width: '100%' }} size="large"
                options={INDICES.map((i) => ({ value: i.value, label: i.value }))} />
              <div style={{ marginTop: 12 }}>
                <Text type="secondary" style={{ fontSize: 13 }}>{currentIndex?.description}</Text>
              </div>
            </Card>
            <Card title={`${selectedIndex} Legend`} style={{ borderRadius: 10 }}>
              {LEGEND_NDVI.map((item) => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <div style={{ width: 20, height: 20, borderRadius: 4, background: item.color, flexShrink: 0 }} />
                  <Text style={{ fontSize: 13 }}>{item.label}</Text>
                </div>
              ))}
            </Card>
            <Card title="Indices Guide" style={{ borderRadius: 10 }}>
              {INDICES.map((idx) => (
                <div key={idx.value} style={{ marginBottom: 8 }}>
                  <Tag color="green">{idx.value}</Tag>
                  <Text style={{ fontSize: 12 }}>{idx.description}</Text>
                </div>
              ))}
            </Card>
          </Space>
        </Col>
      </Row>
    </div>
  );
}
